import { useState } from 'react'
import moment from 'jalali-moment'
import { NumericFormat } from 'react-number-format'
import CustomDatePicker from 'components/datePicker'
import { apiMessage } from 'modules/cashbox/ui'
import { toJalaliInputDate } from 'utilities/helper/jalaliInputDate'
import { faPlain, longJalali, toman } from 'utilities/helper/format'
import {
    firstDateOutOfOrder,
    SCHEDULE_PATTERNS,
    SchedulePattern,
    scheduleSum,
} from '../schedulePatterns'
import type { IResolvedSchedule } from '../useDraftSchedule'

/** rows shown before «نمایش همه» — enough to see the shape of the schedule */
const PREVIEW_ROWS = 4

/** the API's Gregorian YYYY-MM-DD as «۱ آبان ۱۴۰۵» */
export const dueDateLabel = (gregorian: string) =>
    longJalali(
        moment(gregorian, 'YYYY-MM-DD').locale('fa').format('YYYY-MM-DD')
    )

function RowEditor({
    row,
    onSave,
    onCancel,
}: {
    row: IInstallment
    onSave: (next: IInstallment) => void
    onCancel: () => void
}) {
    const [amount, setAmount] = useState(String(row.amount))
    const [dueDate, setDueDate] = useState(row.due_date)
    return (
        <div className=" rounded-xl border border-brand bg-brand-soft p-3">
            <p className=" text-[11px] font-bold text-brand-deep">
                قسط {faPlain.format(row.index)}
            </p>
            <label
                htmlFor={`installment-amount-${row.index}`}
                className=" mt-2 block text-[11px] text-text-secondary"
            >
                مبلغ قسط (تومان)
            </label>
            <NumericFormat
                id={`installment-amount-${row.index}`}
                thousandSeparator
                allowNegative={false}
                inputMode="numeric"
                value={amount}
                onValueChange={(values) => setAmount(values.value)}
                className=" w-full border-b border-stroke bg-transparent py-1.5 text-sm font-bold tabular-nums text-heading outline-none"
            />
            <p className=" mt-2 text-[11px] text-text-secondary">سررسید</p>
            <div dir="rtl">
                <CustomDatePicker
                    value={toJalaliInputDate(dueDate)}
                    setValue={setDueDate}
                    name={`installment-date-${row.index}`}
                    title=""
                    placeHolder="سررسید"
                />
            </div>
            <div className=" mt-3 flex gap-2">
                <button
                    type="button"
                    disabled={!(Number(amount) > 0)}
                    onClick={() =>
                        onSave({
                            ...row,
                            amount: Number(amount),
                            due_date: dueDate,
                        })
                    }
                    className=" flex-1 rounded-full bg-green-1 py-2 text-[12px] font-bold text-white disabled:opacity-50"
                >
                    ثبت تغییر
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    className=" flex-1 rounded-full border border-stroke bg-white py-2 text-[12px] font-bold text-text-secondary"
                >
                    انصراف
                </button>
            </div>
        </div>
    )
}

/**
 * «جدول اقساط» — the schedule the loan will actually carry, before it is
 * created.
 *
 * The first version of the wizard only ever showed «هر قسط حدود X» and sent
 * the server's equal split; the per-instalment edit the old form had was
 * gone. A fund manager asked for it back, and for the usual shapes to be one
 * tap rather than N edits (pwa#127). Tapping a row still edits it by hand.
 *
 * The one rule the server enforces — and enforces SILENTLY, by creating the
 * loan with the equal split instead — is that the rows add up to the exact
 * repayment. So a total that is off is shown in red, with a one-tap fix, and
 * the wizard will not continue until it matches.
 */
export default function ScheduleEditor({
    ready,
    draft,
    isLoading,
    error,
    schedule,
    onPattern,
    onEdit,
    onReset,
    onFixRemainder,
}: {
    ready: boolean
    draft?: IInstallment[]
    isLoading: boolean
    error?: any
    schedule?: IResolvedSchedule
    onPattern: (pattern: SchedulePattern) => void
    onEdit: (rows: IInstallment[], index: number) => void
    onReset: () => void
    onFixRemainder: (rows: IInstallment[]) => void
}) {
    const [editing, setEditing] = useState<number | undefined>()
    const [expanded, setExpanded] = useState(false)

    const rows = schedule?.installments ?? []
    const required = draft ? scheduleSum(draft) : 0
    const total = scheduleSum(rows)
    const gap = required - total
    const badDate = firstDateOutOfOrder(rows)
    const visible =
        expanded || rows.length <= PREVIEW_ROWS + 1
            ? rows
            : rows.slice(0, PREVIEW_ROWS)

    const saveRow = (next: IInstallment) => {
        setEditing(undefined)
        onEdit(
            rows.map((row) => (row.index === next.index ? next : row)),
            next.index
        )
    }

    const fixRemainder = () => {
        const last = rows[rows.length - 1]
        if (!last || last.amount + gap <= 0) return
        onFixRemainder(
            rows.map((row) =>
                row.index === last.index
                    ? { ...row, amount: row.amount + gap }
                    : row
            )
        )
    }

    const errorDetail = error?.response?.data
    const errorText =
        Array.isArray(errorDetail) &&
        errorDetail[0] === 'Interest cannot be more than 4%.'
            ? 'کارمزد نباید بیش از ۴ درصد مبلغ وام باشد.'
            : // a 5xx body is an HTML page, not a sentence
            Number(error?.response?.status) >= 500
            ? 'جدول اقساط ساخته نشد. دوباره تلاش کنید.'
            : apiMessage(error, 'جدول اقساط ساخته نشد. دوباره تلاش کنید.')

    return (
        <div className=" mt-3 rounded-2xl border border-stroke bg-white p-4">
            <div className=" flex items-center justify-between">
                <p className=" text-xs text-text-secondary">جدول اقساط</p>
                {schedule?.edited && (
                    <button
                        type="button"
                        onClick={onReset}
                        className=" text-[11px] font-bold text-brand-deep"
                    >
                        برگشت به الگو
                    </button>
                )}
            </div>

            <div
                role="radiogroup"
                aria-label="الگوی اقساط"
                className=" mt-2 grid grid-cols-4 gap-1.5"
            >
                {SCHEDULE_PATTERNS.map((option) => {
                    const selected =
                        !schedule?.edited &&
                        (schedule?.pattern ?? 'equal') === option.value
                    return (
                        <button
                            key={option.value}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            onClick={() => onPattern(option.value)}
                            className={` rounded-full border py-1.5 text-[11px] font-bold transition-colors ${
                                selected
                                    ? ' border-brand bg-brand-soft text-brand-deep'
                                    : ' border-stroke text-text-secondary'
                            }`}
                        >
                            {option.label}
                        </button>
                    )
                })}
            </div>
            <p className=" mt-1.5 text-[10px] leading-4 text-text-secondary">
                {schedule?.edited
                    ? 'اقساط را دستی تغییر داده‌اید.'
                    : SCHEDULE_PATTERNS.find(
                          (option) =>
                              option.value === (schedule?.pattern ?? 'equal')
                      )?.hint}
                {' — برای تغییر مبلغ یا تاریخ هر قسط، رویش بزنید.'}
            </p>

            {!ready ? (
                <p className=" mt-3 text-[11px] text-text-muted">
                    با وارد کردن مبلغ و تعداد اقساط، جدول اینجا ساخته می‌شود.
                </p>
            ) : error ? (
                <p className=" mt-3 rounded-lg bg-danger-soft px-2 py-1.5 text-[11px] text-danger-on">
                    {errorText}
                </p>
            ) : !draft || isLoading ? (
                <p className=" mt-3 text-[11px] text-text-muted">
                    در حال ساختن جدول اقساط…
                </p>
            ) : (
                <>
                    <ol className=" mt-3 divide-y divide-divider">
                        {visible.map((row) =>
                            editing === row.index ? (
                                <li key={row.index} className=" py-1.5">
                                    <RowEditor
                                        row={row}
                                        onSave={saveRow}
                                        onCancel={() => setEditing(undefined)}
                                    />
                                </li>
                            ) : (
                                <li key={row.index}>
                                    <button
                                        type="button"
                                        onClick={() => setEditing(row.index)}
                                        aria-label={`ویرایش قسط ${faPlain.format(
                                            row.index
                                        )}`}
                                        className=" flex w-full items-center justify-between gap-2 py-2 text-right"
                                    >
                                        <span className=" text-[11px] text-text-secondary">
                                            {/* a real character between the two:
                                                with only a margin, bidi glues «۱»
                                                and «۴ آبان» into «۱۴ آبان» */}
                                            قسط {faPlain.format(row.index)}
                                            {' — '}
                                            <span
                                                className={`${
                                                    badDate === row.index
                                                        ? ' font-bold text-danger-on'
                                                        : ''
                                                }`}
                                            >
                                                {dueDateLabel(row.due_date)}
                                            </span>
                                        </span>
                                        <span className=" whitespace-nowrap text-xs font-bold tabular-nums text-heading">
                                            {toman(row.amount)}
                                        </span>
                                    </button>
                                </li>
                            )
                        )}
                    </ol>
                    {visible.length < rows.length && (
                        <button
                            type="button"
                            onClick={() => setExpanded(true)}
                            className=" mt-1 w-full rounded-full border border-stroke py-1.5 text-[11px] font-bold text-text-secondary"
                        >
                            نمایش همهٔ {faPlain.format(rows.length)} قسط
                        </button>
                    )}

                    {gap !== 0 && (
                        <div className=" mt-2 rounded-lg bg-danger-soft px-2 py-1.5 text-[11px] leading-5 text-danger-on">
                            جمع اقساط {toman(total)} است، ولی باید{' '}
                            {toman(required)} باشد (
                            {gap > 0 ? 'کم' : 'زیاد'}: {toman(Math.abs(gap))}).
                            {rows.length > 0 &&
                                rows[rows.length - 1].amount + gap > 0 && (
                                    <button
                                        type="button"
                                        onClick={fixRemainder}
                                        className=" mt-1.5 block w-full rounded-full bg-white py-1.5 font-bold text-danger-on"
                                    >
                                        اختلاف روی قسط آخر حساب شود
                                    </button>
                                )}
                        </div>
                    )}
                    {badDate !== undefined && (
                        <p className=" mt-2 rounded-lg bg-danger-soft px-2 py-1.5 text-[11px] leading-5 text-danger-on">
                            سررسید قسط {faPlain.format(badDate)} باید بعد از قسط
                            قبلی باشد.
                        </p>
                    )}
                </>
            )}
        </div>
    )
}
